/**
 * ProfileRedirect -- redirects legacy /profile/:uid (numeric) URLs
 * to the canonical /profile/:uuid URL.
 *
 * Extracts the numeric UID from the path, queries `userProfileByUid`
 * to resolve the profile's uuid, then replaces the URL.
 */

import React from 'react'
import { gql, useQuery } from '@apollo/client'
import Loading, { LOADING_ICON_BLUE_DARK_BG_WHITE } from '../../../../Loading/index'
import ErrorPage from '../../../../ErrorPage/index'
import { ERROR_TYPE_404 } from '../../../../ErrorPage/types'

const GET_PROFILE_UUID_BY_UID = gql`
  query ProfileUuidByUid($uid: ID!) {
    userProfileByUid(uid: $uid) {
      uid
      uuid
    }
  }
`

interface ProfileRedirectProps {
  history: { replace: (path: string) => void }
  location: { pathname?: string }
}

const UID_REGEX = /^\/profile\/(\d+)$/

function ProfileRedirect ({ history, location }: ProfileRedirectProps) {
  const pathname = location?.pathname || ''
  const match = pathname.match(UID_REGEX)
  const uid = match ? match[1] : null

  const { data, loading, error } = useQuery(GET_PROFILE_UUID_BY_UID, {
    variables: { uid },
    skip: !uid,
  })

  const uuid = data?.userProfileByUid?.uuid

  React.useEffect(() => {
    if (uuid) history.replace(`/profile/${uuid}`)
  }, [uuid, history])

  if (uuid) {
    return null
  }

  if (loading) {
    return (
      <div className="community-profile-page community-profile-page--loading">
        <Loading visible icon={LOADING_ICON_BLUE_DARK_BG_WHITE} />
      </div>
    )
  }

  if (error || !uid || (!loading && !uuid)) {
    return (
      <ErrorPage
        history={history}
        location={location}
        code={ERROR_TYPE_404}
      />
    )
  }

  return null
}

export default ProfileRedirect
